import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://cygnature-production.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = 'Cygnature Production';

export type SEOProps = {
  title: string;
  description: string;
  /** Path used for canonical + og:url (defaults to current location). */
  path?: string;
  /** Absolute or root-relative image URL for OG/Twitter. */
  image?: string;
  /** ISO date for article published time. */
  datePublished?: string;
  /** ISO date for article modified time. */
  dateModified?: string;
  /** noindex, nofollow control. */
  noindex?: boolean;
  /** Open Graph type, default 'website'. */
  type?: 'website' | 'article' | 'profile';
  /** Optional list of JSON-LD schemas (already objects, not strings). */
  jsonLd?: Array<Record<string, unknown>>;
  /** Locale, default fr_FR. */
  locale?: string;
  /** Author name (for articles). */
  author?: string;
  /** Section/category (for articles). */
  section?: string;
  /** Keywords list (rendered as meta keywords). */
  keywords?: string[];
};

const SEO_MARK = 'data-seo-managed';

const upsertMeta = (selector: string, attrs: Record<string, string>) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(SEO_MARK, 'true');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
};

const upsertLink = (rel: string, href: string, extra: Record<string, string> = {}) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(SEO_MARK, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
};

const clearJsonLd = () => {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${SEO_MARK}="true"]`)
    .forEach((s) => s.remove());
};

const writeJsonLd = (schemas: Array<Record<string, unknown>>) => {
  schemas.forEach((schema) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(SEO_MARK, 'true');
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  });
};

const toAbsoluteUrl = (input?: string) => {
  if (!input) return DEFAULT_OG_IMAGE;
  if (input.startsWith('http')) return input;
  return `${SITE_URL}${input.startsWith('/') ? '' : '/'}${input}`;
};

const SEO = ({
  title,
  description,
  path,
  image,
  datePublished,
  dateModified,
  noindex,
  type = 'website',
  jsonLd = [],
  locale = 'fr_FR',
  author,
  section,
  keywords,
}: SEOProps) => {
  const location = useLocation();
  const resolvedPath = path ?? location.pathname;
  const url = `${SITE_URL}${resolvedPath === '/' ? '' : resolvedPath}`;
  const ogImage = toAbsoluteUrl(image);

  useEffect(() => {
    // Title
    document.title = title;

    // Standard meta
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    });
    if (keywords && keywords.length) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords.join(', ') });
    }

    // Canonical
    upsertLink('canonical', url);

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: title });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: locale });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });
    upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: title });

    // Article-specific OG
    if (type === 'article') {
      if (datePublished) {
        upsertMeta('meta[property="article:published_time"]', {
          property: 'article:published_time',
          content: datePublished,
        });
      }
      if (dateModified) {
        upsertMeta('meta[property="article:modified_time"]', {
          property: 'article:modified_time',
          content: dateModified,
        });
      }
      if (author) {
        upsertMeta('meta[property="article:author"]', { property: 'article:author', content: author });
      }
      if (section) {
        upsertMeta('meta[property="article:section"]', { property: 'article:section', content: section });
      }
    }

    // JSON-LD
    clearJsonLd();
    if (jsonLd.length) writeJsonLd(jsonLd);
  }, [title, description, url, ogImage, noindex, type, datePublished, dateModified, author, section, JSON.stringify(jsonLd), JSON.stringify(keywords), locale]);

  return null;
};

export default SEO;

// ----------------- Schema.org helpers (centralisés) -----------------
export const SITE_BASE_URL = SITE_URL;

export const breadcrumbSchema = (items: Array<{ name: string; path: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path === '/' ? '' : it.path}`,
  })),
});

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  sameAs: [
    'https://www.facebook.com/people/CYgnature-Production-Officiel/100083038225185/',
    'https://www.instagram.com/cygnatureproduction/',
    'https://www.linkedin.com/in/cyrille-lecourt-5425381bb/',
    'https://www.youtube.com/@cygnatureproduction3695',
  ],
});

/**
 * LocalBusiness schema centralisé.
 * IMPORTANT SEO : pour éviter les warnings Google Search Console
 * "L'avis contient plusieurs notes cumulées", `aggregateRating` et `review`
 * ne doivent apparaître QUE sur UNE seule page (la home), jamais sur les
 * pages secondaires (contact, villes…). Toutes les pages référencent la
 * même entité via `@id` (https://cygnature-production.com/#localbusiness).
 */
export const localBusinessSchema = (
  opts: {
    includeRating?: boolean;
    reviews?: Array<{ name: string; rating: number }>;
  } = {},
) => {
  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    image: `${SITE_URL}/og-image.png`,
    telephone: '+33671351550',
    email: 'contact@cygnatureproduction.fr',
    description:
      'Agence événementielle & producteur de talents basée à Troyes. Mariages, galas, spectacles, cérémonies laïques.',
    priceRange: '$$',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Cash, Credit Card, Bank Transfer',
    foundingDate: '2021-04-01',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '8 Rue du Champ de la Gaule',
      addressLocality: 'Saint-Pouange',
      postalCode: '10120',
      addressRegion: 'Grand Est',
      addressCountry: 'FR',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 48.2492, longitude: 4.0503 },
    areaServed: [
      { '@type': 'City', name: 'Troyes' },
      { '@type': 'City', name: 'Reims' },
      { '@type': 'City', name: 'Châlons-en-Champagne' },
      { '@type': 'City', name: 'Auxerre' },
      { '@type': 'City', name: 'Sens' },
      { '@type': 'AdministrativeArea', name: 'Grand Est' },
    ],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '19:00',
      },
    ],
    sameAs: [
      'https://www.facebook.com/people/CYgnature-Production-Officiel/100083038225185/',
      'https://www.instagram.com/cygnatureproduction/',
      'https://www.linkedin.com/in/cyrille-lecourt-5425381bb/',
      'https://www.youtube.com/@cygnatureproduction3695',
    ],
  };

  if (opts.includeRating) {
    base.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '32',
      bestRating: '5',
      worstRating: '1',
    };
  }

  if (opts.reviews && opts.reviews.length) {
    base.review = opts.reviews.map((r) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.name },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(r.rating),
        bestRating: '5',
      },
    }));
  }

  return base;
};

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: 'fr-FR',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/blog?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const serviceSchema = (params: {
  name: string;
  description: string;
  slug: string;
  category?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: params.name,
  description: params.description,
  url: `${SITE_URL}/services/${params.slug}`,
  serviceType: params.category,
  provider: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  areaServed: { '@type': 'Country', name: 'France' },
});

export const articleSchema = (params: {
  title: string;
  description: string;
  slug: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  category?: string;
  keywords?: string[];
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${params.slug}` },
  headline: params.title,
  description: params.description,
  image: toAbsoluteUrl(params.image),
  datePublished: params.datePublished,
  dateModified: params.dateModified ?? params.datePublished,
  author: { '@type': 'Person', name: params.author },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
  },
  articleSection: params.category,
  keywords: params.keywords?.join(', '),
});

export const faqSchema = (items: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((it) => ({
    '@type': 'Question',
    name: it.question,
    acceptedAnswer: { '@type': 'Answer', text: it.answer },
  })),
});

export const collectionPageSchema = (params: {
  name: string;
  description: string;
  path: string;
  itemCount?: number;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: params.name,
  description: params.description,
  url: `${SITE_URL}${params.path}`,
  ...(params.itemCount !== undefined && {
    mainEntity: { '@type': 'ItemList', numberOfItems: params.itemCount },
  }),
});

export const contactPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  url: `${SITE_URL}/contact`,
  name: 'Contact — Cygnature Production',
  mainEntity: localBusinessSchema(),
});
